// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Axis-aligned bounding boxes in board millimetres.
 *
 * Spec §6.2: boxes must be **conservative — never smaller than the true
 * extent**. An undersized box makes an object look "outside the region" when it
 * is not, which defeats the whole verification story. Every constructor here
 * errs large; none of them errs small.
 *
 * `null` means "extent unknown" and is deliberately not collapsed to an empty
 * box — an unknown extent must surface as an unresolved question, never as a
 * confident "outside".
 */

export interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export type MaybeBBox = BBox | null;

export function bbox(minX: number, minY: number, maxX: number, maxY: number): BBox {
  return {
    minX: Math.min(minX, maxX),
    minY: Math.min(minY, maxY),
    maxX: Math.max(minX, maxX),
    maxY: Math.max(minY, maxY),
  };
}

export function fromPoints(points: ReadonlyArray<readonly [number, number]>): MaybeBBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (minX === Infinity) return null;
  return { minX, minY, maxX, maxY };
}

export function union(a: MaybeBBox, b: MaybeBBox): MaybeBBox {
  if (a === null) return b === null ? null : { ...b };
  if (b === null) return { ...a };
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

export function unionAll(boxes: ReadonlyArray<MaybeBBox>): MaybeBBox {
  let out: MaybeBBox = null;
  for (const b of boxes) out = union(out, b);
  return out;
}

/** Grow on all sides. Negative padding is refused — shrinking is never safe. */
export function pad(b: MaybeBBox, amount: number): MaybeBBox {
  if (b === null) return null;
  const a = Math.max(0, amount);
  return { minX: b.minX - a, minY: b.minY - a, maxX: b.maxX + a, maxY: b.maxY + a };
}

export function width(b: BBox): number {
  return b.maxX - b.minX;
}

export function height(b: BBox): number {
  return b.maxY - b.minY;
}

export function center(b: BBox): [number, number] {
  return [(b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2];
}

export function area(b: BBox): number {
  return width(b) * height(b);
}

/** `inner` lies entirely within `outer`, allowing `tol` of float slack. */
export function contains(outer: BBox, inner: BBox, tol = 0): boolean {
  return (
    inner.minX >= outer.minX - tol &&
    inner.minY >= outer.minY - tol &&
    inner.maxX <= outer.maxX + tol &&
    inner.maxY <= outer.maxY + tol
  );
}

/** Any overlap at all, including a shared edge when `tol` > 0. */
export function intersects(a: BBox, b: BBox, tol = 0): boolean {
  return !(a.maxX < b.minX - tol || a.minX > b.maxX + tol || a.maxY < b.minY - tol || a.minY > b.maxY + tol);
}

export function containsPoint(b: BBox, x: number, y: number, tol = 0): boolean {
  return x >= b.minX - tol && x <= b.maxX + tol && y >= b.minY - tol && y <= b.maxY + tol;
}

export function translate(b: MaybeBBox, dx: number, dy: number): MaybeBBox {
  if (b === null) return null;
  return { minX: b.minX + dx, minY: b.minY + dy, maxX: b.maxX + dx, maxY: b.maxY + dy };
}

export function equalsApprox(a: MaybeBBox, b: MaybeBBox, tol = 1e-6): boolean {
  if (a === null || b === null) return a === b;
  return (
    Math.abs(a.minX - b.minX) <= tol &&
    Math.abs(a.minY - b.minY) <= tol &&
    Math.abs(a.maxX - b.maxX) <= tol &&
    Math.abs(a.maxY - b.maxY) <= tol
  );
}

export function formatBBox(b: MaybeBBox): string {
  if (b === null) return 'unknown extent';
  const f = (v: number) => v.toFixed(3).replace(/\.?0+$/, '');
  return `(${f(b.minX)}, ${f(b.minY)}) … (${f(b.maxX)}, ${f(b.maxY)})`;
}
